import React, { useState, useRef } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	InputAdornment,
	Modal,
	TextField,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSelector, useDispatch } from 'react-redux';
import {
	DARK_MODE,
	ZPL_TEMPLATE_FOR_WTCL_PRINT,
	ZPL_TEMPLATE_FOR_WTCL_PRINT_PUNE_JLR,
	isPuneJlr,
	getOkTagMonthYear,
} from '../../constants';
import { useMessageContextManager } from '../MessageOverlay/MessageOverlay';
import { fetchEngineSerialNumber } from '../../redux/actionCreators/qualityDefect';

const getDefaultMonthYear = () =>
	new Date().toLocaleDateString('en-GB', {
		month: '2-digit',
		year: '2-digit',
	});

const getMonthYearFromCrtdDate = (crtdDateStr) => {
	if (!crtdDateStr) return '';
	try {
		// Handle "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss" directly to avoid Timezone shift issues
		if (typeof crtdDateStr === 'string' && crtdDateStr.includes('-')) {
			const dateParts = crtdDateStr.split('T')[0].split('-');
			if (dateParts.length >= 2) {
				const year = dateParts[0].slice(-2);
				const month = dateParts[1].padStart(2, '0');
				return `${month}/${year}`;
			}
		}

		// Fallback for other timestamp formats
		const date = new Date(crtdDateStr);
		if (isNaN(date.getTime())) return '';
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = String(date.getFullYear()).slice(-2);
		return `${month}/${year}`;
	} catch (e) {
		return '';
	}
};

const EMPTY_FORM = {
	vin: '',
	engineSerialNumber: '',
	model: '',
	colour: '',
	kspec: '',
	description: '',
	monthYear: getDefaultMonthYear(),
};

const WtclOkTagTestModal = ({ handleClose }) => {
	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const dispatch = useDispatch();
	const { showMessageOverlay } = useMessageContextManager();
	const isDark = themeMode === DARK_MODE;

	const [form, setForm] = useState(EMPTY_FORM);
	const [vinInput, setVinInput] = useState('');
	const [fetching, setFetching] = useState(false);
	const [vinFetched, setVinFetched] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [previewZplEncoded, setPreviewZplEncoded] = useState('');

	const vinRef = useRef(null);

	// ── Called when user presses Enter or clicks → on VIN field ──────────────
	const handleVinSubmit = () => {
		const vin = vinInput.trim();
		if (!vin) return;

		setFetching(true);
		setVinFetched(false);
		setForm({ ...EMPTY_FORM, vin, monthYear: form.monthYear });

		dispatch(
			fetchEngineSerialNumber(
				vin,
				(data) => {
					setFetching(false);
					setVinFetched(true);
					const rawColour =
						data?.colour ||
						data?.color ||
						data?.vehicleColor ||
						'';
					let trimmedColour = rawColour;
					if (isPuneJlr() && rawColour) {
						const baseColour = rawColour.split('_')[0].trim();
						trimmedColour = baseColour.split(/\s+/).slice(0, 2).join(' ');
					}
					const resolvedVin = data?.serialNumber || vin;
					const resolvedMonthYear = getMonthYearFromCrtdDate(data?.ipmsDate) || getDefaultMonthYear();
					setForm({
						vin: resolvedVin,
						engineSerialNumber: data?.engineSerialNumber || '',
						model:
							data?.description ||
							data?.model ||
							data?.modelCode ||
							data?.vehicleModel ||
							'',
						colour: trimmedColour,
						kspec: data?.kspec || data?.partNumber || data?.vcPartNumber || '',
						description: data?.description || '',
						monthYear: getOkTagMonthYear(resolvedMonthYear, resolvedVin),
					});
				},
				(err) => {
					setFetching(false);
					showMessageOverlay({
						contextText: 'Fetch Failed',
						info:
							err?.response?.data?.message ||
							'Could not fetch engine details for this VIN.',
						error: true,
					});
				},
			),
		);
	};

	const handleVinKeyDown = (e) => {
		if (e.key === 'Enter') handleVinSubmit();
	};

	// ── Print ─────────────────────────────────────────────────────────────────
	const handlePrint = () => {
		if (!form.vin.trim()) {
			showMessageOverlay({ contextText: 'VIN Required', info: 'Please scan VIN first.', error: true });
			return;
		}
		if (!form.engineSerialNumber.trim()) {
			showMessageOverlay({ contextText: 'Engine Number Required', info: 'Engine serial number is missing.', error: true });
			return;
		}
		if (!form.model.trim()) {
			showMessageOverlay({ contextText: 'Model Required', info: 'Model is missing.', error: true });
			return;
		}
		if (!form.colour.trim()) {
			showMessageOverlay({ contextText: 'Colour Required', info: 'Colour is missing.', error: true });
			return;
		}

		const isPune = isPuneJlr();
		const template = isPune
			? ZPL_TEMPLATE_FOR_WTCL_PRINT_PUNE_JLR
			: ZPL_TEMPLATE_FOR_WTCL_PRINT;

		const zpl = template
			.replace(/{VIN}/g, form.vin.trim())
			.replace(/{ENGINE_NUMBER}/g, form.engineSerialNumber.trim())
			.replace(/{MODEL}/g, form.model.trim())
			.replace(/{COLOR}/g, form.colour.trim())
			.replace(/{COLOUR}/g, form.colour.trim())
			.replace(/{KSPEC}/g, form.kspec.trim())
			.replace(/{DESCRIPTION}/g, form.description.trim())
			.replace(/{MONTH_YEAR}/g, form.monthYear.trim() || getDefaultMonthYear());

		const encoded = encodeURIComponent(zpl);

		if (isPune) {
			setPreviewZplEncoded(encoded);
			setShowPreview(true);
		} else {
			window.location.href = `nirmaan://?cmd=print&zplstring=${encoded}`;
			showMessageOverlay({ contextText: 'Printing OK Tag...', success: true });
		}
	};

	const performActualPrint = () => {
		window.location.href = `nirmaan://?cmd=print&zplstring=${previewZplEncoded}`;
		showMessageOverlay({ contextText: 'Printing OK Tag...', success: true });
		setShowPreview(false);
	};

	// ── Shared field sx ───────────────────────────────────────────────────────
	const readonlySx = {
		'& .MuiInputBase-input.Mui-disabled': {
			WebkitTextFillColor: isDark ? '#c0c0c0' : '#333',
			fontWeight: 600,
		},
	};

	return (
		<Modal
			open={true}
			onClose={handleClose}
			sx={{
				background: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Box
				onClick={(e) => e.stopPropagation()}
				sx={{
					position: 'relative',
					background: isDark ? '#151A1E' : '#f2f2f2',
					borderRadius: '12px',
					width: '680px',
					maxWidth: '90vw',
					maxHeight: '90vh',
					overflowY: 'auto',
					padding: '24px',
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
				}}
			>
				{/* ── Header ── */}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography sx={{ fontSize: '24px', fontWeight: 700 }}>
						{showPreview ? 'OK Tag Print Preview' : 'OK Tag Print'}
					</Typography>
					<IconButton onClick={handleClose}>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* ── Preview Mode ── */}
				{showPreview ? (
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
						<Box
							sx={{
								width: '100%',
								backgroundColor: '#fff',
								color: '#000',
								padding: '20px',
								border: '1px solid #ccc',
								borderRadius: '8px',
								display: 'flex',
								flexDirection: 'column',
								gap: 1.5,
								fontFamily: 'monospace',
							}}
						>
							<Typography sx={{ fontSize: '24px', fontWeight: 'bold' }}>
								{isPuneJlr() ? 'PUNE OPERATIONS' : 'PANAPAKKAM OPERATIONS'}
							</Typography>
							<Typography sx={{ fontSize: '18px' }}>INDIA</Typography>

							{/* Barcode mock */}
							<Box sx={{ height: '60px', width: '100%', my: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
								<Box sx={{ width: '80%', height: '40px', background: 'repeating-linear-gradient(90deg, #000, #000 3px, transparent 3px, transparent 6px, #000 6px, #000 8px, transparent 8px, transparent 12px)', mb: 0.5 }} />
								<Typography sx={{ letterSpacing: '2px', fontWeight: 'bold' }}>{form.vin}</Typography>
							</Box>

							{[
								['FULL VIN :-', form.vin],
								['ENG NUMBER :-', form.engineSerialNumber],
								['MODEL :-', form.model],
								['COLOUR :-', form.colour],
								['KSPEC :-', form.kspec || '-'],
								['DESCRIPTION :-', form.description || '-'],
							].map(([label, val]) => (
								<Box key={label} sx={{ display: 'flex' }}>
									<Typography sx={{ width: '150px' }}>{label}</Typography>
									<Typography>{val}</Typography>
								</Box>
							))}

							<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
								<Box sx={{ display: 'flex' }}>
									<Typography sx={{ width: '150px' }}>TYPE OF SALE :-</Typography>
									<Typography>SOLD</Typography>
								</Box>
								<Typography>{form.monthYear}</Typography>
							</Box>
							<Box sx={{ display: 'flex', mt: 1 }}>
								<Typography sx={{ width: '150px' }}>DISTRIBUTER :-</Typography>
								<Typography>TATA MOTORS LTD.</Typography>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, width: '100%' }}>
							<Button variant="outlined" onClick={() => setShowPreview(false)}>Cancel</Button>
							<Button variant="contained" onClick={performActualPrint}>Print</Button>
						</Box>
					</Box>

				) : (
					// ── Form Mode ──
					<React.Fragment>

						{/* Step 1: VIN scan */}
						<TextField
							label="Scan / Enter VIN"
							value={vinInput}
							inputRef={vinRef}
							onChange={(e) => {
								setVinInput(e.target.value);

								if (vinFetched) {
									setVinFetched(false);
									setForm({ ...EMPTY_FORM, vin: e.target.value, monthYear: form.monthYear });
								}
							}}
							onKeyDown={handleVinKeyDown}
							fullWidth
							autoFocus
							placeholder="Scan barcode or type VIN, then press Enter"
							sx={{
								'& .MuiOutlinedInput-root': {
									'& fieldset': {
										borderColor: '#ffffff',
									},
									'&:hover fieldset': {
										borderColor: '#ffffff',
									},
									'&.Mui-focused fieldset': {
										borderColor: '#1976d2',
									},
								},
								'& .MuiInputLabel-root': {
									color: '#ffffff',
								},
								'& .MuiInputLabel-root.Mui-focused': {
									color: '#1976d2',
								},
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{fetching ? (
											<CircularProgress size={22} />
										) : vinFetched ? (
											<CheckCircleOutlineIcon sx={{ color: '#4CAF50' }} />
										) : (
											<Button
												size="small"
												variant="contained"
												onClick={handleVinSubmit}
												disabled={!vinInput.trim()}
												sx={{ minWidth: 0, px: 1.5, py: 0.5 }}
											>
												→
											</Button>
										)}
									</InputAdornment>
								),
							}}
						/>

						{/* API-fetched fields — shown after fetch, read-only */}
						{vinFetched && (
							<>
								<TextField
									label="Engine Serial Number"
									value={form.engineSerialNumber}
									fullWidth
									disabled
									sx={readonlySx}
								/>
								<TextField
									label="Model"
									value={form.model}
									fullWidth
									onChange={(e) =>
										setForm((prev) => ({ ...prev, model: e.target.value }))
									}
								/>
								<TextField
									label="Colour"
									value={form.colour}
									fullWidth
									onChange={(e) =>
										setForm((prev) => ({ ...prev, colour: e.target.value }))
									}
								/>

								{/* Only editable field */}
								<TextField
									label="Month / Year (MM/YY)"
									value={form.monthYear}
									onChange={(e) =>
										setForm((prev) => ({ ...prev, monthYear: e.target.value }))
									}
									fullWidth
								/>
							</>
						)}

						<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
							<Button variant="outlined" onClick={handleClose}>Close</Button>
							<Button
								variant="contained"
								onClick={handlePrint}
								disabled={!vinFetched || fetching}
							>
								Print OK Tag
							</Button>
						</Box>
					</React.Fragment>
				)}
			</Box>
		</Modal>
	);
};

export default WtclOkTagTestModal;